import React, { useState, useEffect, useCallback } from 'react';
import { Button, message, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { createTank, getAllTanksByExploitation, updateTank, deleteTank } from '../../../Utils/exploitations/tanks';
import { createValve, getValvesByExploitation, updateValve, deleteValve} from '../../../Utils/exploitations/valves';
import Loading from '../../Shared/Loading';
import ValveForm from './ValveForm';
import TankForm from './Tankform';
import ValveEditForm from './ValveEditForm';

const { Title } = Typography;

const IrrigationParam = ({ exploitationId, product }) => {
  const [valves, setValves] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewValveForm, setShowNewValveForm] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [valvesData, tanksData] = await Promise.all([
        getValvesByExploitation(exploitationId),
        getAllTanksByExploitation(exploitationId)
      ]);
      setValves(valvesData);
      setTanks(tanksData.data);
    } catch (error) {
      message.error('Erreur lors de la récupération des données');
    } finally {
      setLoading(false);
    }
  }, [exploitationId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleValveCreate = async (valve) => {
    try {
      const newValve = await createValve(valve);
      setValves(prevValves => [...prevValves, newValve]);
      message.success('Vanne créée avec succès');
      setShowNewValveForm(false);
      await fetchData();
    } catch (error) {
      message.error('Erreur lors de la création de la vanne');
    }
  };

  const handleValveUpdate = async (valve) => {
    try {
      await updateValve(valve.valve_id, valve);
      message.success('Vanne mise à jour avec succès');
      await fetchData();
    } catch (error) {
      message.error('Erreur lors de la mise à jour de la vanne');
    }
  };

  const handleTankSubmit = async (tank, index) => {
    try {
      if (tank.tank_id) {
        await updateTank(tank.tank_id, tank);
        message.success('Citerne mise à jour avec succès');
      } else {
        const newTank = await createTank({ ...tank, exploitation_id: exploitationId });
        setTanks(prevTanks => {
          const updatedTanks = [...prevTanks];
          updatedTanks[index] = newTank;
          return updatedTanks;
        });
        message.success('Citerne créée avec succès');
      }
      await fetchData();
    } catch (error) {
      message.error('Erreur lors de l\'opération sur la citerne');
    }
  };

  const handleValveDelete = async (id) => {
    try {
      await deleteValve(id);
      message.success('Vanne supprimée avec succès');
      await fetchData();
    } catch (error) {
      message.error('Erreur lors de la suppression de la vanne');
    }
  };

  const handleTankDelete = async (id) => {
    try {
      await deleteTank(id);
      message.success('Citerne supprimée avec succès');
      await fetchData();
    } catch (error) {
      message.error('Erreur lors de la suppression de la citerne');
    }
  };

  if (!product) {
    message.error('Erreur lors de la récupération des données: produit manquant');
    return null;
  }

  if (!exploitationId) {
    message.error('Erreur lors de la récupération des données: ID d\'exploitation manquant');
    return null;
  }

  if (product.max_valves === undefined || product.max_valves === null) {
    message.error('Erreur lors de la récupération des données: nombre maximum de vannes manquant');
    return null;
  }

  if (product.max_tanks === undefined || product.max_tanks === null) {
    message.error('Erreur lors de la récupération des données: nombre maximum de citernes manquant');
    return null;
  }

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="irrigation-param">
      <Title level={3}>Paramètres d'irrigation</Title>
      <div className='flex flex-col space-y-8'>
        <section>
          <div className='flex justify-between items-center mb-4'>
            <div>
              <Title level={4}>Gestion des vannes</Title>
              <Title level={5} type="secondary">{valves.length}/{product.max_valves} Vannes</Title>
            </div>
            {valves.length < product.max_valves && !showNewValveForm && (
              <Button
                type="primary"
                onClick={() => setShowNewValveForm(true)}
                icon={<PlusOutlined />}
              >
                Ajouter une vanne
              </Button>
            )}
          </div>
          <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4'>
            {valves.map((valve) => (
              <ValveEditForm
                key={valve.valve_id}
                initialValues={valve}
                onSubmit={handleValveUpdate}
                onDelete={handleValveDelete}
              />
            ))}
            {showNewValveForm && (
              <ValveForm
                onSubmit={handleValveCreate}
                onCancel={() => setShowNewValveForm(false)}
                exploitationId={exploitationId}
              />
            )}
          </div>
        </section>

        <section>
          <div className='flex justify-between items-center mb-4'>
            <div>
              <Title level={4}>Gestion des citernes</Title>
              <Title level={5} type="secondary"> {tanks.length}/{product.max_tanks} Citernes </Title>
            </div>
            {tanks.length < product.max_tanks && (
              <Button
                type="primary"
                onClick={() => setTanks([...tanks, {}])}
                icon={<PlusOutlined />}
              >
                Ajouter une citerne
              </Button>
            )}
          </div>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
            {tanks.map((tank, index) => (
              <TankForm
                key={tank.tank_id || `new-${index}`}
                initialValues={tank}
                onSubmit={handleTankSubmit}
                onDelete={handleTankDelete}
                index={index}
              />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default IrrigationParam;